import { BAD_DATE, LOCATION, SPRING_REDIRECT_HEADER } from 'constants/Constants';
import { LOGIN, SECURITY_CHECK_URL, STRAPI_PAYSEND_API } from 'constants/UrlConstants';

import * as Sentry from '@sentry/react';

import { CloudFlareError } from '../components/exceptions/CloudFlareError';
import { ForbiddenError } from '../components/exceptions/ForbiddenError';
import { MiddlewareError } from '../components/exceptions/MiddlewareError';
import { convertFieldsToSearchParams } from '../components/form-multi/utils';
import { amplitudeLogout } from '../components/utils/AmplitudeUtils';

export const getFullEncodeApiUrl = (path = '/') => {
    let url = path;
    if (url.indexOf(SECURITY_CHECK_URL) > 0) {
        url = `${SECURITY_CHECK_URL}`;
    }
    return encodeURI('/api' + url);
};

export const processResponseForCloudFlare = (response, path, options, actions) => {
    if (response.status === 497 && actions?.toggleCloudFlareModal) {
        actions.toggleCloudFlareModal(true, path, options);
        throw new CloudFlareError('CloudFlare error');
    }
};

export function apiFetchData(path = '/', fields, controllerRef, cloudFlareObj = {}, isText = false) {
    const params = new URLSearchParams();
    if (fields) {
        Object.values(fields).forEach((field) => {
            if (field.value) {
                params.append(field.canonicalPath ? field.canonicalPath : field.name, field.value);
            }
        });
    }
    const options = {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: params,
        redirect: 'manual',
        headers: { Accept: 'application/json', 'X-Current-Page': window.location.pathname + window.location.search },
    };
    if (controllerRef) {
        if (!controllerRef.current) {
            controllerRef.current = new AbortController();
        }
        try {
            controllerRef.current.abort();
        } catch (e) {
            Sentry.captureException(e, {
                level: 'log',
            });
        } finally {
            controllerRef.current = new AbortController();
        }
        options.signal = controllerRef.current.signal;
    }
    return fetch(getFullEncodeApiUrl(path), options).then((response) => {
        processResponseForCloudFlare(response, getFullEncodeApiUrl(path), options, cloudFlareObj);
        if (response.status === 403) {
            window.location.replace(LOGIN);
            amplitudeLogout();
            throw new ForbiddenError(`redirect to ${LOGIN}`);
        }
        if (response.type === 'opaqueredirect') {
            window.location.replace(LOGIN);
            throw new Error(`redirect to ${LOGIN}`);
        }
        if (response.headers.has(SPRING_REDIRECT_HEADER)) {
            window.location.replace(response.headers.get(SPRING_REDIRECT_HEADER));
            throw new Error(`redirect to ${response.headers.get(SPRING_REDIRECT_HEADER)}`);
        } else if (response.headers.has(LOCATION)) {
            window.location.replace(response.headers.get(LOCATION));
            throw new Error(`redirect to ${response.headers.get(LOCATION)}`);
        } else if (response.status === 500) {
            throw new MiddlewareError(`response status 500 ${response.statusText}`);
        }
        return isText ? response.text() : response.json();
    });
}

export function apiFetchDataForMessageBody(path = '/', controllerRef) {
    const params = new URLSearchParams();
    const options = {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: params,
        headers: { Accept: 'application/json', 'X-Current-Page': window.location.pathname + window.location.search },
    };
    if (controllerRef) {
        if (!controllerRef.current) {
            controllerRef.current = new AbortController();
        }
        try {
            controllerRef.current.abort();
        } catch (e) {
            Sentry.captureException(e, {
                level: 'log',
            });
            console.warn(e);
        } finally {
            controllerRef.current = new AbortController();
        }
        options.signal = controllerRef.current.signal;
    }
    return fetch(getFullEncodeApiUrl(path), options).then((response) => response.text());
}

export function apiFetchFormData(path = '/', fields, controllerRef) {
    const params = new FormData();
    if (fields) {
        Object.values(fields).forEach((field) => {
            if (field.value) {
                params.append(field.canonicalPath ? field.canonicalPath : field.name, field.value);
            }
        });
    }

    const options = {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: params,
        headers: { Accept: 'application/json', 'X-Current-Page': window.location.pathname + window.location.search },
    };
    if (controllerRef) {
        if (!controllerRef.current) {
            controllerRef.current = new AbortController();
        }
        try {
            controllerRef.current.abort();
        } catch (e) {
            Sentry.captureException(e, {
                level: 'log',
            });
            console.warn(e);
        } finally {
            controllerRef.current = new AbortController();
        }
        options.signal = controllerRef.current.signal;
    }
    return fetch(getFullEncodeApiUrl(path), options).then((response) => {
        if (response.status === 403) {
            window.location.replace(LOGIN);
            amplitudeLogout();
            throw new ForbiddenError(`redirect to ${LOGIN}`);
        }
        if (response.headers.has(SPRING_REDIRECT_HEADER)) {
            window.location.replace(response.headers.get(SPRING_REDIRECT_HEADER));
            throw new Error(`redirect to ${LOGIN}`);
        } else if (response.headers.has(LOCATION)) {
            window.location.replace(response.headers.get(LOCATION));
            throw new Error(`redirect to ${response.headers.get(LOCATION)}`);
        }
        return response.json();
    });
}

export function fetchData(path = '/', method = 'POST', controllerRef) {
    const options = {
        method: method,
        mode: 'cors',
        credentials: 'include',
        headers: { Accept: 'application/json', 'X-Current-Page': window.location.pathname + window.location.search },
    };
    if (controllerRef) {
        if (!controllerRef.current) {
            controllerRef.current = new AbortController();
        }
        try {
            controllerRef.current.abort();
        } catch (e) {
            Sentry.captureException(e, {
                level: 'log',
            });
            console.warn(e);
        } finally {
            controllerRef.current = new AbortController();
        }
        options.signal = controllerRef.current.signal;
    }
    return fetch(getFullEncodeApiUrl(path), options)
        .then((response) => {
            if (response.status === 403) {
                window.location.replace(LOGIN);
                amplitudeLogout();
                throw new ForbiddenError(`redirect to ${LOGIN}`);
            }
            if (response.headers.has(SPRING_REDIRECT_HEADER)) {
                window.location.replace(response.headers.get(SPRING_REDIRECT_HEADER));
                throw new Error(`redirect to ${LOGIN}`);
            } else if (response.headers.has(LOCATION)) {
                window.location.replace(response.headers.get(LOCATION));
                throw new Error(`redirect to ${response.headers.get(LOCATION)}`);
            }
            return response.json();
        })
        .catch((error) => {
            console.warn(error);
            return null;
        });
}

export function apiFetchDataForFlink(path, data) {
    return fetch(getFullEncodeApiUrl(path), {
        method: 'POST',
        mode: 'cors',
        body: convertFieldsToSearchParams(data, true),
        credentials: 'include',
    }).then((response) => response.json());
}

export function apiFetchDataForApplePay(path, data) {
    let params = new URLSearchParams();
    for (const [key, value] of Object.entries(data)) {
        params.append(key, encodeURIComponent(value));
    }
    return fetch(getFullEncodeApiUrl(path), {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        body: params,
        headers: { Accept: 'application/json', 'X-Current-Page': window.location.pathname + window.location.search },
    }).then((response) => response.json());
}

export function apiFetchDataBlob(path, addErrorMessageBody) {
    return fetch(getFullEncodeApiUrl(path), {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: { Accept: 'application/pdf', 'X-Current-Page': window.location.pathname + window.location.search },
    }).then((response) => {
        if (response.status === 500) {
            const message = 'Server Error';
            addErrorMessageBody(message);
            throw new MiddlewareError(message);
        }
        const isErrorWithDate = response.headers.has(BAD_DATE);
        if (isErrorWithDate) {
            const message = response.headers.get(BAD_DATE);
            addErrorMessageBody(message);
        } else {
            return response.blob();
        }
    });
}

export const changeBrowserUrlWithOutReload = (historyURL, json = {}) => {
    if (window && window.history && window.history.pushState) {
        window.history.pushState({ html: json, pageTitle: json.pageTitle }, '', historyURL);
    } else {
        window.location.href = historyURL;
    }
};

export const encodeQueryFields = (fields) => {
    const ret = [];
    Object.keys(fields).forEach((key) => {
        const field = fields[key];
        ret.push(encodeURIComponent(field.canonicalPath ? field.canonicalPath : field.name) + '=' + encodeURIComponent(field.value));
    });

    return ret.join('&');
};

export const getParams = (search) => {
    return new Map(
        search
            .slice(1)
            .split('&')
            .map((kv) => kv.split('=')),
    );
};

export const createInvitedUsersData = async (phoneNumber, inviteCode, lang, page) => {
    const url = new URL(`${STRAPI_PAYSEND_API}/api/v1/${page}/phones`);

    const headers = {
        'Content-Type': 'application/json',
        lang: lang,
    };

    const payload = {
        phone: phoneNumber,
        invite_code: inviteCode,
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
        });
        return await response.json();
    } catch (error) {
        // In case of Strapi CMS is down.
        console.error('Error in createInvitedUsersData', error);
        Sentry.captureException(error, {
            level: 'log',
        });
        return { error: { message: 'Something went wrong. We are working hard to fix the issue' } };
    }
};
